import React, { useState, useEffect, useRef, useCallback } from 'react';
import { parseJsonResponse, numerologyApi } from '../services/api';
import './VoiceMode.css';

const VoiceMode = ({ isOpen, onClose, systemPrompt, numerologyContext, chatMessages = [], onNewMessage }) => {
  const [connectionState, setConnectionState] = useState('idle'); // idle, connecting, connected, error
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentUserTranscript, setCurrentUserTranscript] = useState('');
  const [currentAiTranscript, setCurrentAiTranscript] = useState('');
  const [conversationHistory, setConversationHistory] = useState([]); // Keep history of exchanges
  const [error, setError] = useState(null);
  
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const playbackContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const audioQueueRef = useRef([]);
  const isPlayingRef = useRef(false);
  const nextPlayTimeRef = useRef(0);
  const pcRef = useRef(null);       // RTCPeerConnection (GA Realtime API)
  const dcRef = useRef(null);       // RTCDataChannel for events
  const audioElRef = useRef(null);  // <audio> element for AI playback
  const calculatedPeopleRef = useRef({}); // Store calculated numerology for people mentioned

  // Parse DOB from text (handles both numeric and spoken word formats)
  const parseDOBFromText = (text) => {
    console.log('=== PARSING DOB FROM TEXT ===');
    console.log('Input text:', text);
    
    // Word to number mappings for spoken dates
    const wordToNum = {
      'zero': 0, 'one': 1, 'two': 2, 'three': 3, 'four': 4, 'five': 5,
      'six': 6, 'seven': 7, 'eight': 8, 'nine': 9, 'ten': 10,
      'eleven': 11, 'twelve': 12, 'thirteen': 13, 'fourteen': 14, 'fifteen': 15,
      'sixteen': 16, 'seventeen': 17, 'eighteen': 18, 'nineteen': 19,
      'twenty': 20, 'thirty': 30, 'first': 1, 'second': 2, 'third': 3,
      'fourth': 4, 'fifth': 5, 'sixth': 6, 'seventh': 7, 'eighth': 8,
      'ninth': 9, 'tenth': 10, 'eleventh': 11, 'twelfth': 12,
      'thirteenth': 13, 'fourteenth': 14, 'fifteenth': 15, 'sixteenth': 16,
      'seventeenth': 17, 'eighteenth': 18, 'nineteenth': 19, 'twentieth': 20,
      'twenty-first': 21, 'twenty-second': 22, 'twenty-third': 23, 'twenty-fourth': 24,
      'twenty-fifth': 25, 'twenty-sixth': 26, 'twenty-seventh': 27, 'twenty-eighth': 28,
      'twenty-ninth': 29, 'thirtieth': 30, 'thirty-first': 31
    };
    
    const monthMap = { 
      jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, 
      jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
      january: 1, february: 2, march: 3, april: 4, june: 6,
      july: 7, august: 8, september: 9, october: 10, november: 11, december: 12
    };
    
    // Convert spoken day like "twelve" or "twelfth" to number
    const parseSpokenDay = (word) => {
      if (!word) return null;
      // First try as a number
      const num = parseInt(word);
      if (!isNaN(num) && num >= 1 && num <= 31) return num;
      
      const w = word.toLowerCase().replace(/[- ]/g, '-');
      if (wordToNum[w] !== undefined) return wordToNum[w];
      // Handle "twenty one", "twenty-one", etc.
      const parts = w.split('-');
      if (parts.length === 2 && wordToNum[parts[0]] && wordToNum[parts[1]]) {
        return wordToNum[parts[0]] + wordToNum[parts[1]];
      }
      return null;
    };
    
    // PATTERN 1: Most common - "June 12, 2000" or "June 12 2000" (month name + day number + year number)
    const commonPattern = /\b(january|february|march|april|may|june|july|august|september|october|november|december)\s+(\d{1,2})(?:st|nd|rd|th)?\s*,?\s*(\d{4})\b/i;
    let match = text.match(commonPattern);
    if (match) {
      const month = monthMap[match[1].toLowerCase()];
      const day = parseInt(match[2]);
      const year = parseInt(match[3]);
      console.log('PATTERN 1 MATCH (Month Day Year):', { day, month, year, match: match[0] });
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const result = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        console.log('PARSED DOB:', result);
        return result;
      }
    }
    
    // PATTERN 2: "12 June 2000" or "12th June 2000"
    const dayFirstPattern = /\b(\d{1,2})(?:st|nd|rd|th)?\s+(january|february|march|april|may|june|july|august|september|october|november|december)\s*,?\s*(\d{4})\b/i;
    match = text.match(dayFirstPattern);
    if (match) {
      const day = parseInt(match[1]);
      const month = monthMap[match[2].toLowerCase()];
      const year = parseInt(match[3]);
      console.log('PATTERN 2 MATCH (Day Month Year):', { day, month, year, match: match[0] });
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const result = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        console.log('PARSED DOB:', result);
        return result;
      }
    }
    
    // PATTERN 3: Slash/dash formats - "12/06/2000" or "06-12-2000"
    const slashPattern = /\b(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})\b/;
    match = text.match(slashPattern);
    if (match) {
      // Assume MM/DD/YYYY for US format
      let month = parseInt(match[1]);
      let day = parseInt(match[2]);
      const year = parseInt(match[3]);
      // If first number > 12, it's probably DD/MM/YYYY
      if (month > 12 && day <= 12) {
        [month, day] = [day, month];
      }
      console.log('PATTERN 3 MATCH (Slash format):', { day, month, year, match: match[0] });
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const result = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        console.log('PARSED DOB:', result);
        return result;
      }
    }
    
    // PATTERN 4: ISO format - "2000-06-12"
    const isoPattern = /\b(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})\b/;
    match = text.match(isoPattern);
    if (match) {
      const year = parseInt(match[1]);
      const month = parseInt(match[2]);
      const day = parseInt(match[3]);
      console.log('PATTERN 4 MATCH (ISO format):', { day, month, year, match: match[0] });
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const result = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        console.log('PARSED DOB:', result);
        return result;
      }
    }
    
    // PATTERN 5: Spoken day with month name - "twelfth of June 2000" or "June twelfth 2000"
    const spokenDayPattern1 = /\b(january|february|march|april|may|june|july|august|september|october|november|december)\s+(\w+(?:[- ]\w+)?)\s*,?\s*(\d{4})\b/i;
    match = text.match(spokenDayPattern1);
    if (match) {
      const month = monthMap[match[1].toLowerCase()];
      const day = parseSpokenDay(match[2]);
      const year = parseInt(match[3]);
      console.log('PATTERN 5 MATCH (Month SpokenDay Year):', { day, month, year, match: match[0] });
      if (day && day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const result = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        console.log('PARSED DOB:', result);
        return result;
      }
    }
    
    // PATTERN 6: "twelfth of June 2000"
    const spokenDayPattern2 = /\b(\w+(?:[- ]\w+)?)\s+(?:of\s+)?(january|february|march|april|may|june|july|august|september|october|november|december)\s*,?\s*(\d{4})\b/i;
    match = text.match(spokenDayPattern2);
    if (match) {
      const day = parseSpokenDay(match[1]);
      const month = monthMap[match[2].toLowerCase()];
      const year = parseInt(match[3]);
      console.log('PATTERN 6 MATCH (SpokenDay of Month Year):', { day, month, year, match: match[0] });
      if (day && day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
        const result = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        console.log('PARSED DOB:', result);
        return result;
      }
    }
    
    console.log('NO DOB PATTERN MATCHED');
    return null;
  };

  // Fetch numerology from backend API (admin only)
  const calculateNumerologyFromDOB = async (dobString) => {
    if (!dobString) return null;
    try {
      const { numerology } = await numerologyApi.calculate(dobString);
      if (!numerology) return null;
      const date = new Date(dobString);
      const day = date.getDate();
      const month = date.getMonth() + 1;
      const year = date.getFullYear();
      const today = new Date();
      const age = today.getFullYear() - year - (today < new Date(today.getFullYear(), month - 1, day) ? 1 : 0);
      const topCombos = (numerology.combinations || []).filter(c => c.gridType === 'top');
      const mainCombos = (numerology.combinations || []).filter(c => c.gridType === 'main');
      const bottomCombos = (numerology.combinations || []).filter(c => c.gridType === 'bottom');
      return {
        dob: dobString, day, month, year, age,
        mainGrid: numerology.mainGrid,
        topGrid: numerology.topGrid,
        bottomGrid: numerology.bottomGrid,
        combinations: numerology.combinations,
        professionalQualities: [...new Set(topCombos.map(c => c.meaning).join(', ').split(', '))].slice(0, 12),
        coreQualities: [...new Set(mainCombos.map(c => c.meaning).join(', ').split(', '))].slice(0, 8),
        personalQualities: [...new Set(bottomCombos.map(c => c.meaning).join(', ').split(', '))].slice(0, 12),
      };
    } catch {
      return null;
    }
  };

  // Build context string for a person
  const buildPersonContext = (personData, label) => {
    if (!personData) return '';
    
    const formattedDate = new Date(personData.dob).toLocaleDateString('en-US', { 
      year: 'numeric', month: 'long', day: 'numeric' 
    });

    return `
=== ${label} ===
DOB: ${formattedDate}
Age: ${personData.age} years old
MAIN GRID: ${personData.mainGrid.join('-')} (Grid numbers: ${personData.mainGrid[0]}-${personData.mainGrid[1]}-${personData.mainGrid[2]})

PROFESSIONAL QUALITIES (Top Grid - work/career):
${personData.professionalQualities.join(', ')}

CORE QUALITIES (Main Grid - who they are):
${personData.coreQualities.join(', ')}

PERSONAL QUALITIES (Bottom Grid - relationships/emotions):
${personData.personalQualities.join(', ')}
`;
  };

  // Convert Float32Array to base64-encoded PCM16
  const floatTo16BitPCM = (float32Array) => {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < float32Array.length; i++) {
      let s = Math.max(-1, Math.min(1, float32Array[i]));
      view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer;
  };

  const arrayBufferToBase64 = (buffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, chunk);
    }
    return btoa(binary);
  };

  // Convert base64 PCM16 to Float32Array for playback
  const base64ToFloat32Array = (base64) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    const view = new DataView(bytes.buffer);
    const float32Array = new Float32Array(bytes.length / 2);
    for (let i = 0; i < float32Array.length; i++) {
      const int16 = view.getInt16(i * 2, true);
      float32Array[i] = int16 / (int16 < 0 ? 0x8000 : 0x7fff);
    }
    return float32Array;
  };

  // Play audio from queue
  const playAudioChunk = useCallback(async (audioData) => {
    if (!playbackContextRef.current) {
      playbackContextRef.current = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 24000 });
    }
    
    const playbackContext = playbackContextRef.current;
    
    // Resume audio context if suspended
    if (playbackContext.state === 'suspended') {
      await playbackContext.resume();
    }
    
    try {
      const float32Data = base64ToFloat32Array(audioData);
      const audioBuffer = playbackContext.createBuffer(1, float32Data.length, 24000);
      audioBuffer.getChannelData(0).set(float32Data);
      
      const source = playbackContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(playbackContext.destination);
      
      const currentTime = playbackContext.currentTime;
      const startTime = Math.max(currentTime, nextPlayTimeRef.current);
      source.start(startTime);
      nextPlayTimeRef.current = startTime + audioBuffer.duration;
      
      source.onended = () => {
        if (audioQueueRef.current.length > 0) {
          const nextChunk = audioQueueRef.current.shift();
          playAudioChunk(nextChunk);
        } else {
          isPlayingRef.current = false;
          setIsSpeaking(false);
        }
      };
    } catch (err) {
      console.error('Error playing audio:', err);
    }
  }, []);

  const queueAudio = useCallback((audioData) => {
    audioQueueRef.current.push(audioData);
    if (!isPlayingRef.current) {
      isPlayingRef.current = true;
      setIsSpeaking(true);
      const chunk = audioQueueRef.current.shift();
      playAudioChunk(chunk);
    }
  }, [playAudioChunk]);

  // Initialize WebRTC connection (OpenAI Realtime GA API)
  const connect = useCallback(async () => {
    if (pcRef.current) return;

    setConnectionState('connecting');
    setError(null);

    try {
      // Fetch ephemeral client secret from backend (GA: /v1/realtime/client_secrets)
      const token = localStorage.getItem('token');
      const keyRes = await fetch('/api/ai/realtime-key', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const keyData = await parseJsonResponse(keyRes);
      if (!keyRes.ok) throw new Error(keyData.error || 'Failed to create voice session');
      const { key: ephemeralToken } = keyData;
      if (!ephemeralToken) throw new Error('Voice session not available');

      // Create RTCPeerConnection
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      // Wire up AI audio output to a hidden <audio> element
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioElRef.current = audioEl;
      pc.ontrack = (e) => { audioEl.srcObject = e.streams[0]; };

      // Capture microphone and add track
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Microphone access requires HTTPS.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      mediaStreamRef.current = stream;
      stream.getAudioTracks().forEach(track => pc.addTrack(track, stream));

      // Data channel for sending/receiving Realtime events
      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;

      dc.onopen = () => {
        console.log('Data channel open');
        setConnectionState('connected');

        const sessionConfig = {
          type: 'session.update',
          session: {
            type: 'realtime',
            instructions: `${systemPrompt}\n\n=== CURRENT USER'S NUMEROLOGY DATA ===\n${numerologyContext}\n\n=== VOICE MODE - MANDATORY RULES ===

**RULE 0: LANGUAGE (ABSOLUTE)**
- The user is always speaking English. Always listen, think, and respond in ENGLISH only.
- Regardless of accent, mispronunciation, background noise, silence, or anything that briefly sounds like another language, treat it as English and reply in English.
- NEVER switch to or mirror any other language (no Chinese, Russian, Spanish, French, or any other) under any circumstance.

**RULE 1: BE EXTREMELY CONCISE**
- Maximum 2-3 short sentences per response
- NO lists, NO bullet points, NO numbered items
- NO long explanations - give ONE insight then ask a question
- If you catch yourself going long, STOP immediately

**RULE 2: BE CONVERSATIONAL**
- End with a SHORT question to keep it interactive - but sometimes a powerful statement is enough, no question needed
- ONE insight at a time, then pause for their response

**RULE 3: GRID NUMBERS**
- DO NOT mention grid numbers unless the user EXPLICITLY asks (e.g., "what's their grid?", "what are the numbers?")
- Talk about QUALITIES and TRAITS naturally, not numbers
- ONLY when explicitly asked for the grid, use the EXACT numbers from the [CRITICAL DATA] message
- NEVER make up grid numbers

**RULE 4: NO FORMATTING**
- No emojis, no special characters
- Sound like a friend chatting, not reading a report

**RULE 5: EMOTIONAL DISTRESS IN VOICE**
- When they sound upset, exhausted, or in pain — DO NOT analyze their personality back at them
- Just be present: "I hear you. Take a breath."
- Don't ask probing questions when they're hurting — offer warmth first
- If distress is deep, suggest professional support gently
- Use resonant language, not therapy-speak: "That's a special kind of lonely" not "Your emotional regulation style suggests..."
- If they say stop asking questions, STOP. Just be with them.`,
            output_modalities: ['audio'],
            audio: {
              input: {
                turn_detection: { type: 'semantic_vad' },
                transcription: { model: 'gpt-4o-transcribe', language: 'en', prompt: 'This is an English-language conversation. Transcribe everything in English only.' }
              },
              output: {
                voice: 'marin'
              }
            }
          }
        };
        dc.send(JSON.stringify(sessionConfig));
      };

      dc.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          handleServerEvent(data);
        } catch (err) {
          console.error('Failed to parse event:', err);
        }
      };

      dc.onerror = (e) => {
        console.error('Data channel error:', e);
      };

      pc.onconnectionstatechange = () => {
        const state = pc.connectionState;
        console.log('WebRTC state:', state);
        if (state === 'failed') {
          setError('Connection failed');
          setConnectionState('error');
        }
      };

      // SDP exchange with OpenAI (GA endpoint: /v1/realtime/calls)
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpRes = await fetch('https://api.openai.com/v1/realtime/calls?model=gpt-realtime', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${ephemeralToken}`,
          'Content-Type': 'application/sdp'
        },
        body: offer.sdp
      });

      if (!sdpRes.ok) {
        const errText = await sdpRes.text();
        console.error('SDP exchange failed:', sdpRes.status, errText);
        throw new Error('Failed to establish voice connection');
      }

      const answerSdp = await sdpRes.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

    } catch (err) {
      console.error('Connection error:', err);
      setError(err.message);
      setConnectionState('error');
    }
  }, [systemPrompt, numerologyContext]);

  // Handle server events
  const handleServerEvent = useCallback(async (event) => {
    switch (event.type) {
      case 'session.created':
        console.log('Session created:', event);
        break;
        
      case 'session.updated':
        console.log('Session updated successfully');
        break;
        
      case 'input_audio_buffer.speech_started':
        setIsListening(true);
        setIsSpeaking(false);
        // Clear audio queue when user starts speaking (interruption)
        audioQueueRef.current = [];
        nextPlayTimeRef.current = 0;
        // Clear current transcripts for new exchange (but keep history)
        setCurrentUserTranscript('');
        setCurrentAiTranscript('');
        break;
        
      case 'input_audio_buffer.speech_stopped':
        setIsListening(false);
        break;
        
      case 'input_audio_buffer.committed':
        console.log('Audio committed');
        break;
        
      case 'conversation.item.input_audio_transcription.completed':
        if (event.transcript) {
          const userText = event.transcript;
          setCurrentUserTranscript(userText);
          // Add to main chat and conversation history
          if (onNewMessage) {
            onNewMessage({ role: 'user', content: userText });
          }
          setConversationHistory(prev => [...prev, { role: 'user', content: userText }]);
          
          // Check if user mentioned a DOB - calculate and inject their numerology
          const detectedDOB = parseDOBFromText(userText);
          if (detectedDOB) {
            const personData = await calculateNumerologyFromDOB(detectedDOB);
            if (personData) {
              calculatedPeopleRef.current[detectedDOB] = personData;
              
              console.log('=== CALCULATED NUMEROLOGY ===');
              console.log('DOB:', detectedDOB);
              console.log('Grid:', personData.mainGrid.join('-'));
              console.log('Day:', personData.day, '-> daySum:', personData.mainGrid[0]);
              console.log('=============================');
              
              // Inject the calculated data immediately
              if (dcRef.current && dcRef.current.readyState === 'open') {
                const gridStr = personData.mainGrid.join('-');
                const formattedDate = new Date(detectedDOB).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

                const injectionText = `[CRITICAL DATA - MANDATORY]
PERSON BORN: ${formattedDate}
THEIR GRID IS: ${gridStr}
- First number: ${personData.mainGrid[0]}
- Second number: ${personData.mainGrid[1]}
- Third number: ${personData.mainGrid[2]}
KEY QUALITIES: ${personData.coreQualities.slice(0, 5).join(', ')}

INSTRUCTION: When you respond about this person, you MUST say "their grid is ${gridStr}" - these exact numbers. DO NOT say any other numbers.`;

                dcRef.current.send(JSON.stringify({
                  type: 'conversation.item.create',
                  item: {
                    type: 'message',
                    role: 'system',
                    content: [{
                      type: 'input_text',
                      text: injectionText
                    }]
                  }
                }));
                
                console.log('=== INJECTED DATA ===');
                console.log('For DOB:', formattedDate);
                console.log('Grid:', gridStr);
                console.log('=====================');
              }
            }
          }
        }
        break;
      
      // Handle audio delta events
      case 'response.audio.delta':
      case 'response.output_audio.delta':
        if (event.delta) {
          queueAudio(event.delta);
        }
        break;
        
      case 'response.audio_transcript.delta':
      case 'response.output_audio_transcript.delta':
        if (event.delta) {
          setCurrentAiTranscript(prev => prev + event.delta);
        }
        break;
        
      case 'response.audio_transcript.done':
      case 'response.output_audio_transcript.done':
        // Transcript complete - add to main chat and conversation history
        if (event.transcript) {
          if (onNewMessage) {
            onNewMessage({ role: 'assistant', content: event.transcript });
          }
          // Add AI response to history (user was already added)
          setConversationHistory(prev => [...prev, { role: 'assistant', content: event.transcript }]);
        }
        break;
      
      case 'response.audio.done':
      case 'response.output_audio.done':
        console.log('Audio response complete');
        break;
        
      case 'response.created':
        console.log('Response created');
        // Don't clear AI transcript here - let it build up
        break;
        
      case 'response.done':
        console.log('Response done');
        // DON'T clear transcripts - keep them visible until next speech starts
        break;
        
      case 'error':
        console.error('Server error:', event.error);
        setError(event.error?.message || 'Server error');
        break;
        
      default:
        // console.log('Event:', event.type);
        break;
    }
  }, [queueAudio, onNewMessage]);

  // Start microphone capture
  const startMicrophone = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Microphone access requires HTTPS. Please access the site via HTTPS.');
      }
      
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      mediaStreamRef.current = stream;
      
      // Create AudioContext here (after getUserMedia) for Safari compatibility
      if (!audioContextRef.current) {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        audioContextRef.current = new AudioCtx({ sampleRate: 24000 });
      }
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      
      const audioContext = audioContextRef.current;
      const source = audioContext.createMediaStreamSource(stream);
      sourceRef.current = source;
      
      // Use ScriptProcessorNode for audio processing
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      
      processor.onaudioprocess = (e) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          const inputData = e.inputBuffer.getChannelData(0);
          
          // Downsample if needed
          let outputData;
          if (audioContext.sampleRate !== 24000) {
            const ratio = audioContext.sampleRate / 24000;
            const newLength = Math.round(inputData.length / ratio);
            outputData = new Float32Array(newLength);
            for (let i = 0; i < newLength; i++) {
              outputData[i] = inputData[Math.round(i * ratio)];
            }
          } else {
            outputData = inputData;
          }
          
          const pcmData = floatTo16BitPCM(outputData);
          const base64Audio = arrayBufferToBase64(pcmData);
          
          wsRef.current.send(JSON.stringify({
            type: 'input_audio_buffer.append',
            audio: base64Audio
          }));
        }
      };
      
      // Connect to a silent node (not destination) to avoid feedback
      source.connect(processor);
      const silentGain = audioContext.createGain();
      silentGain.gain.value = 0;
      processor.connect(silentGain);
      silentGain.connect(audioContext.destination);
      
    } catch (err) {
      console.error('Microphone error:', err);
      if (err.message && err.message.includes('HTTPS')) {
        setError('Voice mode requires HTTPS. Please access via https://');
      } else if (err.name === 'NotAllowedError') {
        setError('Microphone access denied. Please allow microphone permission.');
      } else {
        setError(err.message || 'Microphone access failed');
      }
      setConnectionState('error');
    }
  }, []);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    
    if (playbackContextRef.current) {
      playbackContextRef.current.close();
      playbackContextRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (dcRef.current) {
      dcRef.current.close();
      dcRef.current = null;
    }

    if (pcRef.current) {
      pcRef.current.close();
      pcRef.current = null;
    }

    if (audioElRef.current) {
      audioElRef.current.srcObject = null;
      audioElRef.current = null;
    }

    audioQueueRef.current = [];
    isPlayingRef.current = false;
    nextPlayTimeRef.current = 0;
    setIsListening(false);
    setIsSpeaking(false);
    setCurrentUserTranscript('');
    setCurrentAiTranscript('');
  }, []);

  // Handle close
  const handleClose = useCallback(() => {
    cleanup();
    onClose();
  }, [cleanup, onClose]);

  // Connect when modal opens
  useEffect(() => {
    if (isOpen) {
      connect();
    } else {
      cleanup();
    }
    
    return () => {
      cleanup();
    };
  }, [isOpen, connect, cleanup]);

  if (!isOpen) return null;

  const getStatusText = () => {
    if (connectionState === 'connecting') return 'Connecting';
    if (connectionState === 'error') return 'Error';
    if (isListening) return 'Listening';
    if (isSpeaking) return 'Speaking';
    return 'Ready to chat';
  };

  const getStatusClass = () => {
    if (connectionState === 'error') return 'error';
    if (isListening) return 'listening';
    if (isSpeaking) return 'speaking';
    return '';
  };

  // Get the last exchange to show (current or most recent from history)
  const getDisplayTranscripts = () => {
    // If we have current transcripts, show them
    if (currentUserTranscript || currentAiTranscript) {
      return { user: currentUserTranscript, ai: currentAiTranscript };
    }
    // Otherwise show the last exchange from history
    if (conversationHistory.length >= 2) {
      const lastTwo = conversationHistory.slice(-2);
      const userMsg = lastTwo.find(m => m.role === 'user');
      const aiMsg = lastTwo.find(m => m.role === 'assistant');
      return { user: userMsg?.content || '', ai: aiMsg?.content || '' };
    }
    if (conversationHistory.length === 1) {
      const msg = conversationHistory[0];
      return { 
        user: msg.role === 'user' ? msg.content : '', 
        ai: msg.role === 'assistant' ? msg.content : '' 
      };
    }
    return { user: '', ai: '' };
  };

  const displayTranscripts = getDisplayTranscripts();
  const hasTranscript = displayTranscripts.user || displayTranscripts.ai;

  return (
    <div className="voice-mode-overlay">
      <div className="voice-mode-container">
        {/* Header */}
        <div className="voice-header">
          <div className="voice-header-left">
            <h1 className="voice-header-title">HR Assistant</h1>
            <span className="voice-header-subtitle">Voice Mode</span>
          </div>
          <button className="voice-close-btn" onClick={handleClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Status indicator */}
        <div className={`voice-status ${getStatusClass()}`}>
          <span className="status-dot"></span>
          <span>{getStatusText()}</span>
        </div>

        {/* Main visualization - The Orb */}
        <div className="voice-visualization">
          <div className="voice-orb-wrapper">
            <div className={`voice-orb ${isListening ? 'listening' : ''} ${isSpeaking ? 'speaking' : ''} ${connectionState === 'connecting' ? 'connecting' : ''}`}>
            </div>
            <div className="orb-ring"></div>
            <div className="orb-ring"></div>
            <div className="orb-ring"></div>
          </div>
        </div>

        {/* Bottom Section - Transcript and Controls */}
        <div className="voice-bottom-section">
          {connectionState === 'error' ? (
            <div className="voice-error">
              <div className="voice-error-icon">!</div>
              <h3 className="voice-error-title">Connection Failed</h3>
              <p className="voice-error-message">{error || 'Unable to establish voice connection'}</p>
              <button className="voice-retry-btn" onClick={connect}>
                Try Again
              </button>
            </div>
          ) : (
            <>
              {/* Transcript Area */}
              <div className="voice-live-transcript">
                {!hasTranscript ? (
                  <div className="transcript-empty">
                    {connectionState === 'connecting' 
                      ? 'Setting up voice connection...'
                      : 'Start speaking to begin the conversation'}
                  </div>
                ) : (
                  <div className="live-transcript">
                    {displayTranscripts.user && (
                      <div className="transcript-item user">
                        <div className="transcript-avatar">You</div>
                        <div className="transcript-content">
                          <span className="transcript-label">You</span>
                          <span className="transcript-text">{displayTranscripts.user}</span>
                        </div>
                      </div>
                    )}
                    {displayTranscripts.ai && (
                      <div className="transcript-item ai">
                        <div className="transcript-avatar">✨</div>
                        <div className="transcript-content">
                          <span className="transcript-label">HR Assistant</span>
                          <span className="transcript-text">{displayTranscripts.ai}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* End Call Button */}
              <button className="voice-end-btn" onClick={handleClose} title="End call">
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08a.956.956 0 0 1-.29-.7c0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28a11.27 11.27 0 0 0-2.67-1.85.996.996 0 0 1-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"/>
                </svg>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoiceMode;
